import { ChevronDown } from "lucide-react";
import t from "@/lang/lang";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/shared/components/ui/collapsible";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/shared/components/ui/card";
import { TextInputField } from "@/shared/components/field/input-field";
import { EditorInputField } from "@/shared/components/field/editor-input-field";
import { ScopeOfWorkServiceObjectField } from "../../scope-of-work/components/scope-of-work-service-object-field";
import { useOptimisticDebouncedMutation } from "@/shared/lib/debounce/use-optimistic-debounced-mutation";
import {
  PatchScopeOfWorkRequest,
  updateOmniOfferCache,
  useOmniOfferPatchScopeOfWorkMutation,
} from "../api/offer-api";
import { Offer, patchScopeOfWorkOfOffer } from "../types/offer";
import {
  patchScopeOfWork,
  ScopeOfWork,
  ScopeOfWorkPatch,
} from "../../scope-of-work/types/scope-of-work";

/**
 * OfferScopeOfWorkCard edits the data of a scope of work of the offer
 * apart from its positions. Changes are saved as they are typed.
 */
export function OfferScopeOfWorkCard({
  offer,
  scopeOfWork,
  disabled,
}: {
  offer: Offer;
  scopeOfWork: ScopeOfWork;
  disabled: boolean;
}) {
  const request: PatchScopeOfWorkRequest = {
    id: offer.id,
    scopeOfWorkId: scopeOfWork.id,
  };
  const [set] = useOptimisticDebouncedMutation(
    request,
    updateOmniOfferCache,
    useOmniOfferPatchScopeOfWorkMutation(),
  );
  const patch = (p: ScopeOfWorkPatch) =>
    set(
      { ...request, ...p },
      patchScopeOfWorkOfOffer(offer, scopeOfWork.id, (sow) =>
        patchScopeOfWork(sow, p),
      ),
    );
  const fieldName = (field: string) => `scopeOfWork.${scopeOfWork.id}.${field}`;

  return (
    <Collapsible defaultOpen>
      <Card>
        <CardHeader>
          <CollapsibleTrigger className="group flex cursor-pointer items-center gap-2 text-left">
            <CardTitle>{t("Leistungsverzeichnis")}</CardTitle>
            <ChevronDown className="text-muted-foreground size-4 transition-transform group-data-[state=closed]:-rotate-90" />
          </CollapsibleTrigger>
          <CollapsibleContent>
            <CardDescription>
              {t("Bezeichnung und Beschreibung der Leistung.")}
            </CardDescription>
          </CollapsibleContent>
        </CardHeader>
        <CollapsibleContent>
          <CardContent className="grid gap-4 lg:grid-cols-3">
            <TextInputField
              label={t("Titel")}
              text={scopeOfWork.title}
              onTextChange={(title) => patch({ title })}
              fieldName={fieldName("title")}
              fieldProps={{ className: "lg:col-span-1" }}
              disabled={disabled}
            />
            <TextInputField
              label={t("Untertitel")}
              text={scopeOfWork.subtitle}
              onTextChange={(subtitle) => patch({ subtitle })}
              fieldName={fieldName("subtitle")}
              fieldProps={{ className: "lg:col-span-2" }}
              disabled={disabled}
            />
            {offer.serviceObjects.length > 0 && (
              <ScopeOfWorkServiceObjectField
                serviceObjects={offer.serviceObjects}
                value={scopeOfWork.serviceObjectId}
                onValueChange={(serviceObjectId) => patch({ serviceObjectId })}
                fieldName={fieldName("serviceObjectId")}
                disabled={disabled}
              />
            )}
            <EditorInputField
              label={t("Text")}
              initialHtml={scopeOfWork.text}
              onHtmlChange={(text) => patch({ text })}
              fieldName={fieldName("text")}
              fieldProps={{ className: "lg:col-span-3" }}
              disabled={disabled}
            />
          </CardContent>
        </CollapsibleContent>
      </Card>
    </Collapsible>
  );
}
